import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import intl from 'react-intl-universal';
import debounce from 'lodash/debounce';
import { message } from 'antd';
import store from "@/store";
import {
    consumeTicketAfterLoggedInFromSignUp,
    consumeTicketAfterLoggedInFromTokenExpired,
    genLoginUrl,
    genSignUpUrl,
    popupLoginWindow
} from "@/components/SignIn/utils/signin";
import CloseIcon from '../assets/close.svg';
import './SignUpPopup.scss';
import { useCallback } from "react";
import { GA_SIGN_UP_SCENE, sendGA } from "../utils/ga";
import { getQueryVariable, isFromFoxitWebsite } from "@/utils/utils";
import { thirdPartyService } from "@/store/services";
import { AUTO_LOGIN_WAIT_TIME } from '@/limit.constant';

enum MSG_TYPE {
    TO_SIGN_IN = "AccountSignUpToSignIn",
    SIGNED_UP_TO_SIGN_IN = "AccountSignUpSuccess",
    SIGNED_IN = "AccountSignIn",
    SIGN_UP_EXPAND = "AccountSignUpExpand",
    TRIGGER_SIGN_UP_BTN = "AccountSignUpReq",
    SIGN_UP_SUCCEED = "AccountSignUpReqSuccess",
    SIGN_UP_FAIL = "AccountSignUpReqFail",
    LOGIN_PAGE_LOADED = 'LoginPageLoaded',
}

type CommandData = {
    module: MSG_TYPE,
    data?: {
        ticket: string;
        enterpriseId?: string;
        remember_me?: string;
        role?: string;
    }
}

export default function SignUpPopup({
    popupSignUpOrSignIn,
    srcType
}) {
    const { hasSignIn } = store.userStore
    const signUpUrl = useMemo(genSignUpUrl, [])
    const iframeRef = useRef<HTMLIFrameElement>();
    const [popupHeight, setPopupHeight] = useState<number>();
    const [src, setSrc] = useState<string>();
    const signUpExpanded = useRef(false);
    const calcHeight = useCallback((targetHeight) => {
        const height = window.innerHeight;
        if (height <= 705) {
            setPopupHeight(height - 40);
        } else {
            setPopupHeight(targetHeight); // device height enough, fix popup height 671px
        }
    }, []);
    const [autoLoginMsgId, setAutoLoginMsgId] = useState(null);
    const signNoticeFun = () => {
        if (srcType !== 'autoLogin' || !iframeRef.current) {
            return
        }

        const googleDriveParams = getQueryVariable('state');
        const sharePointTokenId = getQueryVariable('SharePointTokenId')
        const { isFromShareLink, isFromServiceModeOfClient } = store.appStore
        const fromFoxitWebsite = isFromFoxitWebsite()

        if(!fromFoxitWebsite) {
            store.userStore.initProcessClosSignUpPopup();
        }

        store.userStore.resetSignUpPopupSrcType()

        if (!googleDriveParams && !sharePointTokenId && !isFromServiceModeOfClient && !isFromShareLink && !fromFoxitWebsite) {
            store.appStore.doUpdateSignInNotice({id:'Permission.SignIn'});
        }
    }
    const autoLoginTimerRef = useRef(null);
    const handleAutoLoginTimeout = useCallback(() => {
        store.userStore.initProcessClosSignUpPopup();
        store.userStore.resetSignUpPopupSrcType();
        destroyAutoLoginMsg();
    }, []);
    const destroyAutoLoginMsg = () => {
        setAutoLoginMsgId(null);
        message.destroy();
    }

    useEffect(() => {
        let srcUrl = ''

        if (srcType === 'autoLogin') {
            srcUrl = genLoginUrl()
        } else {
            srcUrl = signUpUrl
        }
        setSrc(srcUrl);
    }, [srcType])
    useEffect(() => {
        if (srcType !== 'autoLogin') return
        if (!autoLoginTimerRef.current) {
            // auto login timeout
            autoLoginTimerRef.current = setTimeout(handleAutoLoginTimeout, AUTO_LOGIN_WAIT_TIME);
        }

        return () => {
            if (autoLoginTimerRef.current) {
                clearTimeout(autoLoginTimerRef.current);
                autoLoginTimerRef.current = null; // Reset the reference
            }
        };
    }, [srcType]);
    useEffect(() => {
        if (!iframeRef.current) return;

        const handler = (event: MessageEvent<CommandData>) => {
            if (!event.data) {
                return;
            }

            const { module, data } = event.data;
            const { hasSignIn } = store.userStore

            if (!module) return;
            
            switch (module) {
                // click sign up button
                case MSG_TYPE.TO_SIGN_IN:
                    sendGA(GA_SIGN_UP_SCENE.SIGN_UP_POPUP_LOGIN_BTN_CLICKED);
                    popupLoginWindow();
                    store.userStore.initProcessClosSignUpPopup();
                    break;
                // register succeeded, but need login manually. (CN JP area)
                case MSG_TYPE.SIGNED_UP_TO_SIGN_IN:
                    popupLoginWindow();
                    store.userStore.initProcessClosSignUpPopup();
                    break;
                // register succeeded, and auto logged in. (US EU area)
                case MSG_TYPE.SIGNED_IN:
                    if (srcType === 'autoLogin') {
                        if (!hasSignIn) {
                            const params = event.data

                            consumeTicketAfterLoggedInFromTokenExpired(params as any).finally(() => {
                                destroyAutoLoginMsg();
                                store.userStore.initProcessClosSignUpPopup();
                            })
                        }
                    } else {
                        consumeTicketAfterLoggedInFromSignUp(data as any).catch(() => {
                            throw 'consumeTicketAfterLoggedInFromSignUp error'
                        })

                        store.userStore.initProcessClosSignUpPopup();
                    }
                    break;
                case MSG_TYPE.LOGIN_PAGE_LOADED:
                    if (srcType === 'autoLogin' && !hasSignIn) {
                        destroyAutoLoginMsg()
                        signNoticeFun()
                    }
                    break;
                case MSG_TYPE.TRIGGER_SIGN_UP_BTN:
                    sendGA(GA_SIGN_UP_SCENE.SIGN_UP_POPUP_CREATE_BTN_CLICKED);
                    break;
                case MSG_TYPE.SIGN_UP_SUCCEED:
                    sendGA(GA_SIGN_UP_SCENE.SIGN_UP_POPUP_SIGN_UP_SUCCESS);
                    break;
                case MSG_TYPE.SIGN_UP_FAIL:
                    sendGA(GA_SIGN_UP_SCENE.SIGN_UP_POPUP_SIGN_UP_FAIL);
                    break;
                case MSG_TYPE.SIGN_UP_EXPAND:
                    signUpExpanded.current = true;
                    calcHeight(671);
                    break;

                default:
                    break;
            }
        }

        window.addEventListener('message', handler);

        return () => {
            window.removeEventListener('message', handler);
        }
    }, [iframeRef, popupSignUpOrSignIn]);

    useEffect(() => {
        if (popupSignUpOrSignIn) {
            sendGA(GA_SIGN_UP_SCENE.SIGN_UP_POPUP_SHOWN);
        }
    }, [popupSignUpOrSignIn]);

    useEffect(() => {
        if (!iframeRef) return;

        const handler = debounce(function (event: UIEvent) {
            if (!signUpExpanded.current) return;
            calcHeight(671);
        }, 300);

        window.addEventListener('resize', handler);
        return () => {
            window.removeEventListener('resize', handler);
        }
    }, [iframeRef]);

    useEffect(() => {
        if (srcType === 'autoLogin' && !autoLoginMsgId) {
            const messageId = message.loading(intl.get('AutoLogin'), 0);

            setAutoLoginMsgId(messageId)
        }
    }, [srcType])

    function onClose() {
        sendGA(GA_SIGN_UP_SCENE.SIGN_UP_POPUP_CLOSED);
        store.userStore.initProcessClosSignUpPopup();
    }
    // TODO FOXIT 云编辑不使用此登录判断弹窗；
    return null;


    if (!popupSignUpOrSignIn || hasSignIn) return null;

    return createPortal(
        <div className={`fv_embedded-sign-up ${srcType === 'autoLogin' && 'opacity-zero'}`}>
            {/* if signUp hasn't been expanded, use normal height 474px(in CSS) */}
            <div className="fv_embedded-sign-up__inner" style={!signUpExpanded.current ? null : { height: `${popupHeight}px` }}>
                <div className="fv_embedded-sign-up__header">
                    <div className="fv_embedded-sign-up__header-title">
                        {intl.get('user.signUp')}
                    </div>
                    <img className="fv_embedded-sign-up__header-close" onClick={onClose} src={CloseIcon} alt="icon" />
                </div>
                <iframe
                    className="fv_embedded-sign-up__frame"
                    ref={iframeRef}
                    src={src}
                ></iframe>
            </div>
        </div>
        ,
        document.body
    )
}
